// src/components/group/ScheduleSection.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

function ScheduleSection({ 
  group, 
  groupId, 
  token, 
  apiUrl, 
  onUpdated,
  isOpen,
  onToggle 
}) {
  const { t, i18n } = useTranslation();
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [selectedSkips, setSelectedSkips] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [loadingMeetings, setLoadingMeetings] = useState(true);

  // ✅ UPPDATERAD - Re-fetch när kritiska inställningar ändras
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchUpcomingMeetings();
    setSelectedSkips(group.skippedMeetings || []);
  }, [
    group.invitationAdvance, 
    group.reminderAdvance, 
    group.frequency, 
    group.meetingFrequency?.dayOfWeek, 
    group.meetingFrequency?.time,
    groupId,
    apiUrl,
    token
  ]);

  const fetchUpcomingMeetings = async () => {
    setLoadingMeetings(true);
    try {
      const response = await fetch(`${apiUrl}/api/groups/${groupId}/upcoming-meetings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        setLoadingMeetings(false);
        return;
      }
      
      const data = await response.json();
      setUpcomingMeetings(data.meetings || []);
    } catch (error) {
      console.error('Error fetching upcoming meetings:', error);
    } finally {
      setLoadingMeetings(false);
    }
  };

  const handleActivate = async () => {
    if (!window.confirm(t('schedule.confirmActivate'))) return;

    setProcessing(true);

    try {
      const response = await fetch(`${apiUrl}/api/groups/${groupId}/activate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          skippedMeetings: selectedSkips
        })
      });

      const data = await response.json();

      if (response.ok) {
        alert(`✅ ${data.message}`);
        onUpdated(); // Detta uppdaterar redan gruppen
      } else {
        alert(`❌ ${data.message}`);
      }
    } catch (error) {
      console.error('Error activating group:', error);
      alert(t('schedule.couldNotActivate'));
    } finally {
      setProcessing(false);
    }
  };

  const handlePause = async () => {
    if (!window.confirm(t('schedule.confirmPause'))) return;

    setProcessing(true);

    try {
      const response = await fetch(`${apiUrl}/api/groups/${groupId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          status: 'paused'
        })
      });

      if (response.ok) {
        alert(t('schedule.schedulePaused'));
        onUpdated();
      } else {
        alert(t('schedule.couldNotPause'));
      }
    } catch (error) {
      console.error('Error pausing group:', error);
      alert(t('schedule.somethingWentWrong'));
    } finally {
      setProcessing(false);
    }
  };

  const handleResume = async () => {
    if (!window.confirm(t('schedule.confirmResume'))) return;

    setProcessing(true);

    try {
      const response = await fetch(`${apiUrl}/api/groups/${groupId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          status: 'active'
        })
      });

      if (response.ok) {
        alert(t('schedule.scheduleResumed'));
        onUpdated();
      } else {
        alert(t('schedule.couldNotResume'));
      }
    } catch (error) {
      console.error('Error resuming group:', error);
      alert(t('schedule.somethingWentWrong'));
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdateSkips = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/groups/${groupId}/skip-meetings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          skippedMeetings: selectedSkips
        })
      });

      if (response.ok) {
        alert(t('schedule.meetingsUpdated'));
        onUpdated();
      } else {
        alert(t('schedule.couldNotUpdateMeetings'));
      }
    } catch (error) {
      console.error('Error updating skipped meetings:', error);
    }
  };

  const toggleSkip = (dateISO) => {
    if (selectedSkips.includes(dateISO)) {
      setSelectedSkips(selectedSkips.filter(d => d !== dateISO));
    } else {
      setSelectedSkips([...selectedSkips, dateISO]);
    }
  };

  const isDraft = group.status === 'draft';
  const isActive = group.status === 'active';
  const isPaused = group.status === 'paused';

  const hasChanges = JSON.stringify(selectedSkips.sort()) !== JSON.stringify((group.skippedMeetings || []).sort());

  return (
    <section className="settings-section schedule-status-section">
      <div className="section-header" onClick={onToggle}>
        <h2>
          {t('schedule.title')}
          {isDraft && <span className="status-badge draft">{t('schedule.statusInactive')}</span>}
          {isActive && <span className="status-badge active">{t('schedule.statusActive')}</span>}
          {isPaused && <span className="status-badge paused">{t('schedule.statusPaused')}</span>}
        </h2>
        <span className="toggle-icon">{isOpen ? '▼' : '▶'}</span>
      </div>
      {isOpen && (
        <div className="schedule-content">
          {/* Status info */}
          <div className="schedule-status-info">
            {isDraft && (
              <p>{t('schedule.draftMessage')}</p>
            )}
            {isActive && (
              <p>
                {t('schedule.activeMessage')}
                {upcomingMeetings.length > 0 && (() => {
                  const now = new Date();
                  
                  // ⭐ FIX: Loop igenom ALLA möten tills vi hittar en kommande händelse
                  for (const meeting of upcomingMeetings) {
                    // Skippa möten som är markerade som skippade
                    if (selectedSkips.includes(meeting.dateISO)) continue;
                    
                    const meetingDate = new Date(meeting.dateISO);
                    
                    // Beräkna alla email-tidpunkter MED EXPLICIT TID
                    const invitationDate = new Date(meetingDate);
                    invitationDate.setDate(invitationDate.getDate() - (group.invitationAdvance || 7));
                    invitationDate.setHours(9, 0, 0, 0); // 09:00 samma dag
                    
                    const decisionDate = new Date(meetingDate);
                    decisionDate.setDate(decisionDate.getDate() - 1);
                    const [hours, minutes] = (group.decisionTime || '18:00').split(':');
                    decisionDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
                    
                    // ⭐ FIXAT: Ta bort påminnelse från visningen
                    // Backend sköter påminnelser smart (bara till de som inte svarat)
                    // Frontend kan inte veta detta, så vi visar bara:
                    // 1. Inbjudan (om inte skickad)
                    // 2. Beslut (alltid)
                    // 3. Möte (alltid)
                    
                    // ✅ FIX: Use translations for event labels
                    const events = [
                      { 
                        type: 'invitation', 
                        date: invitationDate, 
                        emoji: '📧', 
                        label: t('schedule.eventLabels.invitation')
                      },
                      // Påminnelse visas INTE (backend hanterar smart)
                      { 
                        type: 'decision', 
                        date: decisionDate, 
                        emoji: '✅', 
                        label: t('schedule.eventLabels.decision')
                      },
                      { 
                        type: 'meeting', 
                        date: meetingDate, 
                        emoji: '🎉', 
                        label: t('schedule.eventLabels.meeting')
                      }
                    ];
                    
                    // Hitta NÄSTA händelse som inte har passerat för DETTA möte
                    const nextEvent = events.find(event => event.date > now);
                    
                    if (nextEvent) {
                      // ✅ Hittade en kommande händelse! Visa den.
                      // ✅ FIX: Use i18n.language instead of localStorage
                      const locale = i18n.language === 'en' ? 'en-US' : 'sv-SE';
                      
                      // Formatera händelsens datum och tid
                      const dateStr = nextEvent.date.toLocaleDateString(locale, {
                        weekday: 'long',
                        month: 'long',
                        day: 'numeric'
                      });
                      const timeStr = nextEvent.date.toLocaleTimeString(locale, {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false // ⭐ Använd 24-timmars format
                      });
                      
                      // ⭐ NY: Formatera mötetsdatum (vilket möte händelsen gäller)
                      const meetingDateStr = meetingDate.toLocaleDateString(locale, {
                        day: 'numeric',
                        month: 'long'
                      });
                      
                      // ✅ FIX: Use translations for messages
                      if (nextEvent.type === 'meeting') {
                        // För mötet självt: "🎉 Meeting: Friday, November 27 at 12:00"
                        return ` ${nextEvent.emoji} ${t('schedule.eventMessages.meeting', {
                          label: nextEvent.label,
                          date: dateStr,
                          time: timeStr
                        })}`;
                      } else {
                        // För inbjudan/beslut: "📧 Invitation for meeting November 27 will be sent: Monday, November 17 at 09:00"
                        return ` ${nextEvent.emoji} ${t('schedule.eventMessages.other', {
                          label: nextEvent.label,
                          meetingDate: meetingDateStr,
                          date: dateStr,
                          time: timeStr
                        })}`;
                      }
                    }
                    
                    // Om nextEvent är null, fortsätt till nästa möte (alla händelser för detta möte har passerat)
                  }
                  
                  // Om vi kommer hit har inga kommande händelser för några möten
                  return null;
                })()}
              </p>
            )}
            {isPaused && (
              <p>{t('schedule.pausedMessage')}</p>
            )}
          </div>

          {/* Upcoming meetings */}
          <div className="upcoming-meetings-box">
            <h3 style={{ marginLeft: '10px' }}>{t('schedule.upcomingMeetings')}</h3>
            <p style={{ 
              color: '#ffffff', 
              fontSize: '14px', 
              marginBottom: '20px',
              marginLeft: '10px',
              lineHeight: '1.6'
            }}>
              {t('schedule.skipInstructions')}
            </p>
            
            {loadingMeetings ? (
              <p className="loading-message">{t('schedule.loadingMeetings')}</p>
            ) : upcomingMeetings.length === 0 ? (
              <div className="no-meetings-message">
                <p>{t('schedule.noUpcomingMeetings')}</p>
                <p style={{ fontSize: '14px', color: '#666', marginTop: '10px' }}>
                  {t('schedule.addMeetingInfo')}
                </p>
              </div>
            ) : (
              <>
                {upcomingMeetings.map((meeting, index) => {
                  // ✅ FIX: Format date in frontend using current language
                  const meetingDate = new Date(meeting.dateISO);
                  const locale = i18n.language === 'en' ? 'en-US' : 'sv-SE';
                  const formattedDate = meetingDate.toLocaleDateString(locale, {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  });
                  
                  return (
                    <label key={index} className="meeting-checkbox-item">
                      <input
                        type="checkbox"
                        checked={selectedSkips.includes(meeting.dateISO)}
                        onChange={() => toggleSkip(meeting.dateISO)}
                      />
                      <span className={selectedSkips.includes(meeting.dateISO) ? 'skipped-text' : ''}>
                        {formattedDate} {t('schedule.atTime')} {meeting.time}
                      </span>
                      {index === 0 && !selectedSkips.includes(meeting.dateISO) && (
                        <span className="first-badge">{t('schedule.nextMeeting')}</span>
                      )}
                    </label>
                  );
                })}

                {hasChanges && !isDraft && (
                  <button 
                    onClick={handleUpdateSkips}
                    className="btn-update-skips"
                  >
                    {t('schedule.saveChanges')}
                  </button>
                )}
              </>
            )}
          </div>

          {/* Action buttons */}
          <div className="schedule-actions">
            {isDraft && (
              <button 
                onClick={handleActivate}
                disabled={processing}
                className="btn-schedule-activate"
              >
                {processing ? t('schedule.activating') : t('schedule.activateButton')}
              </button>
            )}

            {isActive && (
              <button 
                onClick={handlePause}
                disabled={processing}
                className="btn-schedule-pause"
              >
                {processing ? t('schedule.pausing') : t('schedule.pauseButton')}
              </button>
            )}

            {isPaused && (
              <button 
                onClick={handleResume}
                disabled={processing}
                className="btn-schedule-resume"
              >
                {processing ? t('schedule.resuming') : t('schedule.resumeButton')}
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

export default ScheduleSection;
